// src/prototype/components/StatTile.tsx
// Compact KPI tile — icon chip + uppercase label + value — used in the
// use-case drilldown signal row and report summaries. Normalized from the
// inline signal-row tiles in EAUseCaseBriefPage; the "dominant" variant is the
// dark hero tile (e.g. Annual ROI), "default" is the light bordered tile.
import React from 'react';

export interface StatTileProps {
  /** Uppercase eyebrow label, e.g. "Time to Value". */
  label: string;
  /** Primary value string, e.g. "82 / 100" or "$1.2M–$1.8M". */
  value: React.ReactNode;
  /** Optional leading icon (typically a lucide icon). */
  icon?: React.ReactNode;
  /** "dominant" = dark navy hero tile; "default" = light bordered tile. */
  variant?: 'default' | 'dominant';
  /** Let the value wrap (up to two lines) instead of truncating. Opt-in; default false. */
  wrap?: boolean;
  className?: string;
}

export const StatTile: React.FC<StatTileProps> = ({ label, value, icon, variant = 'default', wrap = false, className = '' }) => {
  const valueFit = wrap ? 'line-clamp-2' : 'truncate';
  if (variant === 'dominant') {
    return (
      <div className={`bg-[#18365E] rounded-xl p-4 flex items-center gap-3 ${className}`}>
        {icon && (
          <div className="w-9 h-9 rounded-lg bg-white/20 flex items-center justify-center shrink-0 text-white">
            {icon}
          </div>
        )}
        <div className="min-w-0">
          <p className="text-[9px] font-semibold text-white/60 uppercase tracking-wide">{label}</p>
          <p className={`text-base font-bold text-white leading-tight ${valueFit}`}>{value}</p>
        </div>
      </div>
    );
  }
  return (
    <div className={`bg-white rounded-xl border border-[#e5e7eb] p-4 flex items-center gap-3 ${className}`}>
      {icon && (
        <div className="w-8 h-8 rounded-lg bg-[#18365E]/10 flex items-center justify-center shrink-0 text-[#18365E]">
          {icon}
        </div>
      )}
      <div className="min-w-0">
        <p className="text-[9px] font-semibold text-[#717182] uppercase tracking-wide">{label}</p>
        <p className={`text-sm font-bold text-[#18365E] ${valueFit}`}>{value}</p>
      </div>
    </div>
  );
};

export default StatTile;
